import { useParams } from "react-router";
import useProjectDetails from "./hooks/useProjectDetails";
import {
  EventFragment,
  ProjectFragment,
  ProjectType,
} from "../../../../__generated__/graphql";
import FullLoading from "../../../../components/FullLoading";
import { motion } from "motion/react";
import { projectTypeToName } from "../../../../lib/utils";
import { useEffect, useState, useRef } from "react";
import { useQueryState, parseAsString } from "nuqs";
import EventSelection from "./components/EventSelection";
import { useUser } from "../../../../contexts/UserContext/hooks/useUser";
import Button from "../../../../components/Button";
import ProjectModal from "../../components/ProjectModal";
import CreateEventButton from "../../../events/components/CreateEventButton";
import {
  CalendarIcon,
  ChevronRightIcon,
  CubeIcon,
  DocumentIcon,
  EllipsisHorizontalIcon,
  PhoneIcon,
  StarIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import EventDetails from "../../../../components/EventDetails";
import TabBar, { Tab } from "../../../../components/TabBar";
import ProjectPlanningContent from "./components/ProjectPlanning";
import DocumentItem from "../../../documents/components/DocumentItem";
import ProjectParticipants from "./components/ProjectParticipants";
import CreateDocumentModal from "../../../../components/CreateDocumentModal";
import useContacts from "../../../contacts/hooks/useContacts";
import ContactItem from "../../../contacts/components/ContactItem";
import CreateContactModal from "../../../contacts/components/CreateContactModal";

const tabs = [
  {
    label: "Planning",
    value: "planning",
    icon: <CalendarIcon className="w-4 h-4" />,
  },
  {
    label: "Évènements",
    value: "events",
    icon: <StarIcon className="w-4 h-4" />,
  },
  {
    label: "Documents",
    value: "documents",
    icon: <DocumentIcon className="w-4 h-4" />,
  },
  {
    label: "Participants",
    value: "participants",
    icon: <UserGroupIcon className="w-4 h-4" />,
  },
  {
    label: "Contacts",
    value: "contacts",
    icon: <PhoneIcon className="w-4 h-4" />,
  },
];

export default function ProjectPage() {
  const [tabValue, setTabValue] = useQueryState(
    "tab",
    parseAsString.withDefault("planning")
  );
  const [eventId, setEventId] = useQueryState("eventId", parseAsString);

  const activeTab = tabs.find((t) => t.value === tabValue) ?? tabs[0];
  const setActiveTab = (tab: Tab) => setTabValue(tab.value);

  const [isCreateContactModalOpen, setIsCreateContactModalOpen] =
    useState(false);
  const { currentOrganization } = useUser();

  const { id } = useParams();
  const {
    data,
    loading,
    error,
    refetch: refetchProjectDetails,
  } = useProjectDetails(id);

  const { data: contactsData, refetch: refetchContacts } = useContacts({
    projectId: id,
  });

  const [selectedEvent, setSelectedEvent] = useState<EventFragment | null>(
    null
  );
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false);
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] =
    useState(false);

  const selectedEventRef = useRef<HTMLDivElement>(null);
  const isOrganizationOwner =
    data?.project?.organizationId === currentOrganization?.id;

  useEffect(() => {
    const foundEvent = data?.project?.events?.find(
      (event) => event?.id === eventId
    );

    if (!foundEvent) return;

    setSelectedEvent(foundEvent);
  }, [data, eventId]);

  useEffect(() => {
    // Scroll to the selected event only on page load
    if (selectedEventRef.current && eventId) {
      selectedEventRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) return <FullLoading />;
  if (error) return <div>Error: {error.message}</div>;

  if (!data || !data.project)
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold">Projet non trouvé</h1>
      </div>
    );

  const handleEventClick = (event: EventFragment) => {
    setSelectedEvent(event);
    setEventId(event.id);
  };

  const handleNavigateToEventDetails = (event: EventFragment) => {
    setTabValue("events");
    setSelectedEvent(event);
    setEventId(event.id);
  };

  const projectHeader = (project: ProjectFragment) => {
    return (
      <div className="flex items-center gap-2">
        <div className="text-sm text-secondary">Projets</div>

        <ChevronRightIcon className="w-4 h-4 text-secondary" />

        <div className="flex items-center gap-2 border border-input  rounded-md p-2 ">
          <CubeIcon className="w-4 h-4 text-accent" />

          <div className="text-sm text-accent">
            {project.projectType === ProjectType.Other
              ? project.otherProjectType
              : projectTypeToName[project.projectType]}
          </div>

          <div className="text-sm text-primary" data-testid="project-name">{project.name}</div>
        </div>

        {isOrganizationOwner ? (
          <EllipsisHorizontalIcon
            className="w-6 h-6 text-primary hover:text-secondary cursor-pointer"
            data-testid="project-edit-button"
            onClick={() => {
              setIsCreateProjectModalOpen(true);
            }}
          />
        ) : null}
      </div>
    );
  };

  const eventsContent = () => {
    return (
      <div className="flex flex-col gap-4 border border-input rounded-md p-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center">
            <div className="text-lg font-bold">Évènements</div>
            {isOrganizationOwner ? (
              <CreateEventButton
                variant="secondary"
                projectId={id}
                onEventCreated={(event) => {
                  handleEventClick(event);
                }}
                buttonText="Ajouter un évènement"
              />
            ) : null}
          </div>
          {data?.project?.events?.length === 0 ? (
            <div className="text-sm text-tertiary">
              Créez le premier évènement de ce projet
            </div>
          ) : null}

          <div className="flex overflow-x-auto gap-4 custom-scrollbar">
            {data?.project?.events?.map((event) => {
              if (!event) return null;

              return (
                <EventSelection
                  key={event?.id}
                  event={event}
                  isSelected={event.id === eventId}
                  handleEventClick={handleEventClick}
                  selectedEventRef={selectedEventRef}
                />
              );
            })}
          </div>
        </div>

        <div className="w-full">
          {data?.project?.events &&
          data?.project?.events.length > 0 &&
          !selectedEvent ? (
            <div className="text-sm text-gray-500">
              Sélectionnez un évènement pour voir les détails
            </div>
          ) : null}

          <EventDetails
            event={selectedEvent}
            refetchProjectDetails={refetchProjectDetails}
          />
        </div>
      </div>
    );
  };

  const planningContent = () => {
    if (!data.project) return null;
    return (
      <ProjectPlanningContent
        project={data.project}
        onNavigateToEventDetails={handleNavigateToEventDetails}
      />
    );
  };

  const documentsContent = () => {
    const projectDocuments = data?.project?.documents?.filter(
      (document) => document !== null
    );

    return (
      <div className="w-full rounded-md p-4 border border-input">
        <div className="flex justify-between items-center">
          <div className="flex flex-col flex-1">
            <div className="text-lg font-bold text-primary">Documents</div>
            <div className="text-sm text-secondary">
              Liste des documents liés à ce projet
            </div>
          </div>

          <Button
            variant="secondary"
            className="text-sm"
            onClick={() => setIsCreateDocumentModalOpen(true)}
          >
            + Document
          </Button>
        </div>

        <div className="flex gap-2 mt-4">
          {projectDocuments?.length === 0 ? (
            <div className="text-sm text-tertiary">
              Pas de document pour le moment
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {projectDocuments?.map((document) => {
              if (!document) return null;
              return <DocumentItem key={document?.id} document={document} />;
            })}
          </div>
        </div>
      </div>
    );
  };

  const contactsContent = () => {
    return (
      <>
        <CreateContactModal
          projectId={id}
          isOpen={isCreateContactModalOpen}
          onClose={() => {
            setIsCreateContactModalOpen(false);
          }}
        />

        <div className="w-full rounded-md p-4 border border-input">
          <div className="flex justify-between items-center">
            <div className="text-lg font-bold text-primary">Contacts</div>
            <Button
              variant="secondary"
              className="text-sm"
              onClick={() => setIsCreateContactModalOpen(true)}
            >
              + Ajouter un contact
            </Button>
          </div>

          <div className="flex gap-2 mt-4">
            {contactsData?.contacts.length === 0 ? (
              <div className="text-sm text-tertiary">
                Pas de contact pour le moment
              </div>
            ) : null}

            {contactsData?.contacts.map((contact) => {
              if (!contact) return null;
              return (
                <ContactItem
                  key={contact.id}
                  contact={contact}
                  onDelete={() => {
                    refetchContacts();
                  }}
                />
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const participantsContent = () => {
    if (!data.project) return null;

    return <ProjectParticipants project={data.project} refetchProject={refetchProjectDetails} />;
  };

  return (
    <>
      <CreateDocumentModal
        isOpen={isCreateDocumentModalOpen}
        onClose={() => setIsCreateDocumentModalOpen(false)}
        projectId={id}
        onSubmit={() => {
          setIsCreateDocumentModalOpen(false);
        }}
        canBeSharedWithMultiplePerson={true}
      />

      <ProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => {
          setIsCreateProjectModalOpen(false);
        }}
        defaultProject={data.project}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col gap-4"
        data-testid="project-detail-page"
      >
        {projectHeader(data.project)}

        {data.project.description ? (
          <p className="text-sm text-gray-500 max-w-[500px] max-h-[100px] overflow-y-auto custom-scrollbar">
            {data.project.description}
          </p>
        ) : null}

        <TabBar tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

        <motion.div
          key={activeTab.value}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab.value === "events" && eventsContent()}
          {activeTab.value === "planning" && planningContent()}
          {activeTab.value === "documents" && documentsContent()}
          {activeTab.value === "participants" && participantsContent()}
          {activeTab.value === "contacts" && contactsContent()}
        </motion.div>
      </motion.div>
    </>
  );
}
